import * as _ from 'lodash-es';
import React, { useEffect, useState } from 'react';
import type { SelectEntityOptionProps } from '../components/SelectEntityOption';
import SelectEntityOption from '../components/SelectEntityOption';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import LocalSearchHighlight from '../search/LocalSearchHighlight';
import {
  PostMatchKey,
  unflattenRefIndex,
} from '../search/entitySearch/localEntitySearch';
import { useAllTopicMap } from '../topic/useAllTopics';
import type { FuzzySearchMatch } from '../utils/search/fuzzySearch';
import PostBreadcrumbs from './PostBreadcrumbs';
import PostDetailPreviewPopOver from './PostDetailPreviewPopOver';
import { PostEntityIcon } from './PostIcon';
import PostTitleWithIcons from './PostTitleWithIcons';
import type { SlimPostWithTopics } from './useAllSlimPosts';

function getMostFrequentTopicIndex(
  topicIndexes: (number | undefined)[],
): number {
  const frequencyMap = new Map<number, number>();
  let max = 0;
  let maxElement = 0;

  topicIndexes.forEach((topicIndex) => {
    if (typeof topicIndex !== 'number') return;

    const frequency = (frequencyMap.get(topicIndex) || 0) + 1;
    frequencyMap.set(topicIndex, frequency);

    if (frequency > max) {
      max = frequency;
      maxElement = topicIndex;
    }
  });

  return maxElement;
}

const POST_SELECT_OPTION_POST_FRAGMENT = gql(`
  fragment PostSelectOption_post on Post {
    id
    title
    topics {
      id
    }
    ...PostEntityIcon_post
    ...PostTitleWithIcons_post
  }
`);

type MatchWithTopicIndex = FuzzySearchMatch & {
  topicIndex?: number;
};

interface PostSelectOptionProps extends Omit<SelectEntityOptionProps, 'label'> {
  matches: readonly FuzzySearchMatch[];
  post:
    | SlimPostWithTopics
    | DocumentType<typeof POST_SELECT_OPTION_POST_FRAGMENT>;
  focused?: boolean;
  disablePreview?: boolean;
}

const OPEN_DELAY = 500;

const PostSelectOption = React.forwardRef<
  HTMLDivElement,
  PostSelectOptionProps
>(
  (
    {
      matches,
      post,
      focused = false,
      disablePreview = false,
      ...props
    }: PostSelectOptionProps,
    ref,
  ) => {
    const topicMap = useAllTopicMap();
    const topics = _.compact(post.topics.map(({ id }) => topicMap[id]));
    const matchesWithTopicIndex = topics
      ? matches.map<MatchWithTopicIndex>((match) => {
          if (typeof match.refIndex === 'number') {
            switch (match.key) {
              case PostMatchKey.TopicNames:
                return { ...match, topicIndex: match.refIndex };
              case PostMatchKey.AncestorTopicNames: {
                const { innerIndex, outerIndex } = unflattenRefIndex(
                  topics,
                  'ancestors',
                  match.refIndex,
                );
                return {
                  ...match,
                  topicIndex: outerIndex,
                  refIndex: innerIndex,
                };
              }
              default:
                break;
            }
          }

          return match;
        })
      : null;

    const bestTopicIndex = getMostFrequentTopicIndex(
      _.map(matchesWithTopicIndex, 'topicIndex'),
    );

    const breadcrumbsTopic = topics?.[bestTopicIndex];

    const content = (
      <SelectEntityOption
        {...props}
        icon={
          <PostEntityIcon post={post} bordered={!!post.archivedAt && focused} />
        }
        secondaryTop={
          breadcrumbsTopic ? (
            <PostBreadcrumbs
              truncate
              topic={breadcrumbsTopic}
              names={[
                <LocalSearchHighlight
                  key="topicNames"
                  matchKey={PostMatchKey.TopicNames}
                  refIndex={bestTopicIndex}
                  matches={matches}
                  defaultValue={breadcrumbsTopic.name}
                />,
                ...(breadcrumbsTopic.ancestors || []).map((ancestor, index) => (
                  <LocalSearchHighlight
                    key={ancestor.id}
                    matchKey={PostMatchKey.AncestorTopicNames}
                    refIndex={index}
                    matches={matchesWithTopicIndex?.filter(
                      (match) => match.topicIndex === bestTopicIndex,
                    )}
                    defaultValue={ancestor.name}
                  />
                )),
              ]}
            />
          ) : null
        }
        label={
          <PostTitleWithIcons
            title={
              <LocalSearchHighlight
                matches={matches}
                matchKey={PostMatchKey.Title}
                defaultValue={post.title}
              />
            }
            post={post}
            iconSize="small"
          />
        }
        ref={ref}
      />
    );

    const shouldOpenPopOver = focused && !disablePreview;
    const [isPopOverOpen, setIsPopOverOpen] = useState(false);

    useEffect(() => {
      if (!shouldOpenPopOver) {
        setIsPopOverOpen(false);
        return () => undefined;
      }

      const timeout = setTimeout(() => {
        setIsPopOverOpen(true);
      }, OPEN_DELAY);
      return () => clearTimeout(timeout);
    }, [shouldOpenPopOver]);

    return (
      <PostDetailPreviewPopOver
        postId={post.id}
        open={isPopOverOpen}
        onOpenChange={setIsPopOverOpen}
      >
        {content}
      </PostDetailPreviewPopOver>
    );
  },
);

export default PostSelectOption;
